// The lesson screen (EDU-9, ARCH §29.3, key `KeyJ`). Client-only presentation
// (ENG-1) over `game.eduActiveLessonDef()`/`game.eduResult()` — the evaluator and
// its saved progress are the sim/host's, this only renders them. Modeled on
// `notebook.ts`'s pattern: a bare "off" panel with no active lesson, else a list.
//
// No quiz screen exists yet (M51 ships `gradeQuiz`/`submitQuiz` as data + a debug
// seam, not a UI — D-TF-258): this screen lists objectives only.
import { h } from '../h';
import { criterionId } from '../../../sim/education/objectives';
import type { Game } from '../../boot';

export function openLessonScreen(game: Game): void {
  const root = h('div', { class: 'bb-screen', 'data-testid': 'lesson-screen' });
  document.body.append(root);
  game.uiOpen = true;
  document.exitPointerLock();
  const close = (): void => {
    document.removeEventListener('keydown', onKey, true);
    root.remove();
    game.uiOpen = false;
    void game.renderer.domElement.requestPointerLock();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);

  const lesson = game.eduActiveLessonDef();
  const result = game.eduResult();
  if (!lesson || !result) {
    root.append(
      h('div', { class: 'bb-title' }, 'Lesson'),
      h('div', { class: 'bb-panel', 'data-testid': 'lesson-off' }, 'No lesson is running in this world.'),
    );
    return;
  }

  const body = h('div', {}, ...lesson.objectives.map((o) => {
    const tick = result.objectives[o.name];
    const done = tick !== null && tick !== undefined;
    const need = o.criteria.reduce((n, c) => n + (c.count ?? 1), 0);
    const have = o.criteria.reduce((n, c) => {
      const key = `${o.name}::${criterionId(c)}`;
      return n + Math.min(result.counts.find(([k]) => k === key)?.[1] ?? 0, c.count ?? 1);
    }, 0);
    return h(
      'div',
      { class: 'bb-notebook-row', 'data-testid': `lesson-objective-${o.name}` },
      done ? '✓ ' : `${have} / ${need} `,
      o.title,
      o.hint ? h('div', { class: 'bb-muted' }, o.hint) : '',
    );
  }));

  // Hangar §39.5 (M105.3) — `.tflesson`: this screen's own export, the lesson definition
  // plus the player's live result, through the Hangar pickers (or a download, unbridged).
  // Export only: nothing in this build lets a player *start* an arbitrary lesson from a
  // file (lessons come from the content registry, §29), so there is nothing an Import
  // button here would actually do — logged as D-TF-296 rather than built speculatively.
  const exportBtn = h('button', { class: 'bb-btn', 'data-testid': 'lesson-export' }, 'Export') as HTMLButtonElement;
  exportBtn.onclick = () => {
    void (async () => {
      const { exportFile } = await import('../../hangar/files');
      const bytes = new TextEncoder().encode(JSON.stringify({ lesson, result }, null, 2));
      await exportFile(game.hangar, bytes, `${lesson.name}.tflesson`, ['.tflesson'], 'application/json');
    })();
  };

  root.append(
    h('div', { class: 'bb-title' }, lesson.title),
    h(
      'div',
      { class: 'bb-panel' },
      h('div', { class: 'bb-muted' }, lesson.description),
      body,
      h('div', { class: 'bb-script-actions' }, exportBtn, h('button', { class: 'bb-btn bb-primary', onclick: close }, 'Close')),
    ),
  );
}
